import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Loader2, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ParagraphDiff } from './ParagraphDiff';
import {
  hasTaApprovalSubmitter,
  taProposalWasDelivered,
  submitTaApproval,
  taProposalIsSettled,
} from './taApprovalBridge';
import {
  restoredFromTaOutcome,
  type TaProposalPhase,
  type TaProposalReceipt,
  type TechnicalAuditProposal,
} from './taProposalTypes';

const SUPPORTED_ISSUE_TYPES = new Set(['thin_content']);

const VISIBLE_PARAGRAPHS = 3;

const pagePath = (pageUrl: string): string => {
  try {
    return new URL(pageUrl).pathname || pageUrl;
  } catch {
    return pageUrl;
  }
};

const proposalExpired = (proposal: TechnicalAuditProposal): boolean => {
  if (!proposal.expiresAt) return false;
  const expiry = Date.parse(proposal.expiresAt);
  return Number.isFinite(expiry) && expiry <= Date.now();
};

const initialPhase = (proposal: TechnicalAuditProposal): TaProposalPhase => {
  if (proposal.capability !== 'supported') return 'blocked';
  if (!SUPPORTED_ISSUE_TYPES.has(proposal.issueType)) return 'blocked';
  if (!proposal.approvalRequestId) return 'unavailable';
  if (proposalExpired(proposal)) return 'unavailable';
  if (!taProposalWasDelivered(proposal.proposalId)) return 'unavailable';
  if (!hasTaApprovalSubmitter()) return 'unavailable';
  if (taProposalIsSettled(proposal.proposalId)) return 'unavailable';
  return 'awaiting_approval';
};

const blockedCopy = (proposal: TechnicalAuditProposal): string => {
  if (proposal.capability !== 'supported') {
    return proposal.blockedReason ?? 'This page cannot be changed from here.';
  }
  return `Nothing here knows how to apply a ${proposal.issueType} proposal.`;
};

const unavailableCopy = (proposal: TechnicalAuditProposal): string => {
  if (taProposalIsSettled(proposal.proposalId)) {
    return 'This proposal was already submitted once in this session.';
  }
  if (!proposal.approvalRequestId) {
    return 'The server did not attach an approval identity to this proposal, so it cannot be approved from here.';
  }
  if (proposalExpired(proposal)) {
    return 'This proposal has expired, so it can no longer be approved from here.';
  }
  if (!taProposalWasDelivered(proposal.proposalId)) {
    return 'This proposal arrived in an earlier session, so whether it was already applied is not known here. Ask in the chat before acting on it again.';
  }
  return 'No approval channel is registered in this build, so this cannot be applied from here.';
};

interface NoticeProps {
  tone: 'blocked' | 'failed';
  icon: ReactNode;
  title: string;
  body: string;
}

function Notice({ tone, icon, title, body }: NoticeProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-md border px-3 py-2.5',
        tone === 'failed'
          ? 'border-destructive/30 bg-destructive/5'
          : 'border-border bg-muted/50',
      )}
    >
      <span
        className={cn(
          'mt-0.5 shrink-0',
          tone === 'failed' ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <div
          className={cn(
            'text-xs font-semibold',
            tone === 'failed' ? 'text-destructive' : 'text-foreground',
          )}
        >
          {title}
        </div>
        <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{body}</div>
      </div>
    </div>
  );
}

function Receipt({ receipt, total }: { receipt: TaProposalReceipt; total: number }) {
  const appliedCount = receipt.appliedParagraphIds.length;
  if (receipt.status === 'failed') {
    return (
      <Notice
        tone="failed"
        icon={<AlertTriangle className="h-3.5 w-3.5" />}
        title="The apply failed"
        body={
          receipt.message ??
          'The server reported a failure. Nothing on the page is assumed to have changed.'
        }
      />
    );
  }
  return (
    <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5">
      <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
        <Check className="h-3.5 w-3.5" />
        <span>
          Applied {appliedCount} of {total} paragraphs
        </span>
      </div>
      {receipt.skipped.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {receipt.skipped.map((skip) => (
            <li key={skip.id} className="text-xs leading-relaxed text-muted-foreground">
              Skipped {skip.id}: {skip.reason}
            </li>
          ))}
        </ul>
      )}
      {receipt.message && (
        <div className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
          {receipt.message}
        </div>
      )}
    </div>
  );
}

export interface TechnicalAuditProposalCardProps {
  proposal: TechnicalAuditProposal;
  className?: string;
  startPhase?: TaProposalPhase;
  startReceipt?: TaProposalReceipt | null;
}

export function TechnicalAuditProposalCard({
  proposal,
  className,
  startPhase,
  startReceipt,
}: TechnicalAuditProposalCardProps) {
  const [phase, setPhase] = useState<TaProposalPhase>(
    () => startPhase ?? initialPhase(proposal),
  );
  const [receipt, setReceipt] = useState<TaProposalReceipt | null>(startReceipt ?? null);
  const [failure, setFailure] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const total = proposal.paragraphs.length;
  const shown = expanded ? proposal.paragraphs : proposal.paragraphs.slice(0, VISIBLE_PARAGRAPHS);
  const hidden = total - shown.length;
  const path = useMemo(() => pagePath(proposal.target.pageUrl), [proposal.target.pageUrl]);

  const decide = useCallback(
    async (decision: 'approve' | 'decline') => {
      if (phase !== 'awaiting_approval') return;
      const approvalRequestId = proposal.approvalRequestId;
      if (!approvalRequestId) {
        setPhase('unavailable');
        return;
      }
      setFailure(null);
      if (decision === 'approve') setPhase('applying');
      const controller = new AbortController();
      abortRef.current = controller;
      const outcome = await submitTaApproval(
        {
          proposalId: proposal.proposalId,
          approvalRequestId,
          decision,
        },
        controller.signal,
      );
      abortRef.current = null;

      if (!outcome.ok) {
        setFailure(outcome.reason);
        setPhase('failed');
        return;
      }
      const settled = restoredFromTaOutcome(outcome, decision);
      setReceipt(settled.receipt);
      setPhase(settled.phase);
    },
    [phase, proposal],
  );

  const terminal =
    phase === 'applied' || phase === 'failed' || phase === 'declined' || phase === 'pending';

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-card',
        className,
      )}
      data-testid="ta-proposal-card"
      data-phase={phase}
    >
      <div className="flex items-start justify-between gap-3 border-b border-border px-3 py-2.5">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-foreground">
            Rewrite {total} {total === 1 ? 'paragraph' : 'paragraphs'}
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{path}</div>
        </div>
        <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          One approval
        </span>
      </div>

      <div className="space-y-3 px-3 py-3">
        {phase === 'blocked' && (
          <Notice
            tone="blocked"
            icon={<ShieldOff className="h-3.5 w-3.5" />}
            title="Blocked"
            body={blockedCopy(proposal)}
          />
        )}
        {phase === 'unavailable' && (
          <Notice
            tone="blocked"
            icon={<ShieldOff className="h-3.5 w-3.5" />}
            title="Cannot be approved here"
            body={unavailableCopy(proposal)}
          />
        )}
        {phase === 'failed' && failure && (
          <Notice
            tone="failed"
            icon={<AlertTriangle className="h-3.5 w-3.5" />}
            title="Not applied"
            body={failure}
          />
        )}
        {phase === 'pending' && (
          <Notice
            tone="blocked"
            icon={<ShieldOff className="h-3.5 w-3.5" />}
            title="Outcome unknown"
            body="The approval result is not confirmed yet. Nothing on the page is assumed to have changed."
          />
        )}
        {receipt && <Receipt receipt={receipt} total={total} />}

        {shown.map((paragraph, index) => (
          <ParagraphDiff
            key={paragraph.id}
            before={paragraph.before}
            after={paragraph.after}
            location={paragraph.location}
            index={index + 1}
            total={total}
            applied={phase === 'applied' && receipt?.appliedParagraphIds.includes(paragraph.id)}
          />
        ))}

        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
          >
            Show {hidden} more {hidden === 1 ? 'paragraph' : 'paragraphs'}
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2.5">
        <div className="min-w-0 text-[11px] text-muted-foreground">
          {phase === 'applying' && 'Applying…'}
          {phase === 'applied' && 'Applied'}
          {phase === 'declined' && 'Declined'}
          {phase === 'failed' && 'Not applied'}
          {phase === 'pending' && 'Outcome unknown'}
          {(phase === 'awaiting_approval' || phase === 'blocked' || phase === 'unavailable') && (
            <span className="tabular-nums">
              {total} {total === 1 ? 'paragraph' : 'paragraphs'} in one batch
            </span>
          )}
        </div>
        {phase === 'awaiting_approval' && (
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => decide('decline')}>
              Decline
            </Button>
            <Button size="sm" onClick={() => decide('approve')}>
              Approve and apply
            </Button>
          </div>
        )}
        {phase === 'applying' && (
          <Button size="sm" disabled className="shrink-0">
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            Applying
          </Button>
        )}
        {terminal && (
          <span className="shrink-0 text-[11px] font-medium text-muted-foreground">
            {phase === 'applied'
              ? 'Nothing further to approve'
              : phase === 'pending'
                ? 'Nothing is assumed written'
                : 'No change was made'}
          </span>
        )}
      </div>
    </div>
  );
}
